import type { Option } from '@app/src/components/Form/Select/SimpleSelect'
import SimpleSelect, { isSingleSelectValue } from '@app/src/components/Form/Select/SimpleSelect'
import { SustainabilityArea } from '@app/src/types/resourceExplorer'
import { Box, Button, Stack } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import type { AreaFilter, RiskIndexFilterState, StatusFilter, TopicFilter } from './riskIndexFilters'
import { hasRiskIndexFilters, NO_RISK_INDEX_FILTERS } from './riskIndexFilters'

interface RiskIndexFilterBarProps {
  filters: RiskIndexFilterState
  onChange: (filters: RiskIndexFilterState) => void
  /** Topic select is only rendered when options are supplied — standalone indices have no topic. */
  topicOptions?: Option<TopicFilter>[]
}

const RiskIndexFilterBar: React.FC<RiskIndexFilterBarProps> = ({ filters, onChange, topicOptions }) => {
  const { formatMessage } = useIntl()
  const [isAreaSelectOpen, setAreaSelectOpen] = useState(false)
  const [isTopicSelectOpen, setTopicSelectOpen] = useState(false)
  const [isStatusSelectOpen, setStatusSelectOpen] = useState(false)

  const allLabel = formatMessage({ id: 'riskScreeningConfig.cards.filterAll' })

  const areaOptions = useMemo<Option<AreaFilter>[]>(
    () => [
      { label: allLabel, value: 'all' },
      { label: formatMessage({ id: 'schemas.risk.esgArea.Environment' }), value: SustainabilityArea.Environment },
      { label: formatMessage({ id: 'schemas.risk.esgArea.Social' }), value: SustainabilityArea.Social },
      { label: formatMessage({ id: 'schemas.risk.esgArea.Governance' }), value: SustainabilityArea.Governance },
    ],
    [formatMessage, allLabel],
  )
  const statusOptions = useMemo<Option<StatusFilter>[]>(
    () => [
      { label: allLabel, value: 'all' },
      { label: formatMessage({ id: 'riskScreeningConfig.cards.active' }), value: 'active' },
      { label: formatMessage({ id: 'riskScreeningConfig.cards.inactive' }), value: 'inactive' },
    ],
    [formatMessage, allLabel],
  )
  const allTopicOptions = useMemo<Option<TopicFilter>[]>(
    () => [{ label: allLabel, value: 'all' }, ...(topicOptions ?? [])],
    [topicOptions, allLabel],
  )

  return (
    <Stack direction="row" spacing={1} mb={3} alignItems="flex-end">
      <Box width={200}>
        <SimpleSelect<AreaFilter>
          multiple={false}
          size="small"
          loading={false}
          disableClearable
          open={isAreaSelectOpen}
          setOpen={setAreaSelectOpen}
          options={areaOptions}
          fieldLabel={formatMessage({ id: 'schemas.riskSetting.esgArea' })}
          value={areaOptions.find(option => option.value === filters.area) ?? areaOptions[0]}
          onChange={option => {
            if (isSingleSelectValue(option)) onChange({ ...filters, area: option.value })
          }}
        />
      </Box>
      {topicOptions && (
        <Box width={200}>
          <SimpleSelect<TopicFilter>
            multiple={false}
            size="small"
            loading={false}
            disableClearable
            open={isTopicSelectOpen}
            setOpen={setTopicSelectOpen}
            options={allTopicOptions}
            fieldLabel={formatMessage({ id: 'general.topic' })}
            value={allTopicOptions.find(option => option.value === filters.topic) ?? allTopicOptions[0]}
            onChange={option => {
              if (isSingleSelectValue(option)) onChange({ ...filters, topic: option.value })
            }}
          />
        </Box>
      )}
      <Box width={200}>
        <SimpleSelect<StatusFilter>
          multiple={false}
          size="small"
          loading={false}
          disableClearable
          open={isStatusSelectOpen}
          setOpen={setStatusSelectOpen}
          options={statusOptions}
          fieldLabel={formatMessage({ id: 'general.status' })}
          value={statusOptions.find(option => option.value === filters.status) ?? statusOptions[0]}
          onChange={option => {
            if (isSingleSelectValue(option)) onChange({ ...filters, status: option.value })
          }}
        />
      </Box>
      <Button variant="text" onClick={() => onChange(NO_RISK_INDEX_FILTERS)} disabled={!hasRiskIndexFilters(filters)}>
        {formatMessage({ id: 'general.clearFilters' })}
      </Button>
    </Stack>
  )
}

export default RiskIndexFilterBar
